import CustomFormLabel from "Shared/Components/Inputs/CustomFormLabel";
import { Stack, type SxProps, type Theme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { type ReactNode, useState } from "react";
import {
	type Control,
	Controller,
	type FieldError,
	type FieldErrors,
	type FieldPath,
	type FieldValues,
	get,
} from "react-hook-form";
import { FormInputContainer } from "../Container/FormInputContainer";
import { useFieldError } from "../Helpers/getFormErrors";

type AutocompleteOption = {
	value: string | number;
	label: string | number;
	optionLabel?: ReactNode;
	group?: string;
};

type InputProps<FormType extends FieldValues> = {
	id: string;
	name: FieldPath<FormType>;
	control: Control<FormType, object>;
	errors: FieldErrors<FormType>;
	options: AutocompleteOption[];
	numeric?: boolean;
	label?: string;
	fullWidth?: boolean;
	disabled?: boolean;
	readOnly?: boolean;
	sxLabel?: SxProps<Theme>;
	onChange?: (value: AutocompleteOption | null) => void;
	groupBy?: (option: AutocompleteOption) => string;
};

export const FormAutocomplete = <FormType extends FieldValues>({
	id,
	label,
	fullWidth = false,
	name,
	control,
	errors,
	options,
	numeric,
	disabled,
	readOnly,
	sxLabel,
	groupBy,
}: InputProps<FormType>) => {
	const [isOptionListOpen, setIsOptionListOpen] = useState(false);
	const { hasError, errorMessage } = useFieldError({
		fieldError: get(errors, name) as FieldError,
	});

	return (
		<>
			{label && (
				<CustomFormLabel sx={sxLabel} htmlFor={id}>
					{label}
				</CustomFormLabel>
			)}
			<Controller
				name={name}
				control={control}
				render={({ field: { onChange, value } }) => {
					return (
						<FormInputContainer>
							<Autocomplete
								id={id}
								options={options || []}
								disabled={disabled}
								onClose={() => setIsOptionListOpen(false)}
								onOpen={() => setIsOptionListOpen(true)}
								open={isOptionListOpen}
								groupBy={groupBy}
								getOptionLabel={(option) =>
									typeof option.label === "string" ? option.label : "NO LABEL"
								}
								renderOption={(_props, option, { selected }) => {
									return (
										<Stack
											key={option.value}
											direction="row"
											p={1}
											onClick={() => {
												numeric
													? onChange(Number(option.value))
													: onChange(option.value || "");
												setIsOptionListOpen(false);
											}}
											sx={{
												cursor: "pointer",
												backgroundColor: selected
													? "action.selected"
													: "transparent",
												"&:hover": {
													backgroundColor: "action.hover",
												},
											}}
										>
											{option.optionLabel ?? option.label}
										</Stack>
									);
								}}
								value={
									options?.find((opt) =>
										numeric ? opt.value === Number(value) : opt.value === value,
									) || null
								}
								onChange={(_, newValue) => {
									numeric
										? onChange(newValue ? Number(newValue?.value) : null)
										: onChange(newValue?.value || "");
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										error={!!hasError}
										helperText={hasError ? errorMessage : ""}
										fullWidth={fullWidth}
										InputProps={{ ...params.InputProps, readOnly }}
									/>
								)}
							/>
						</FormInputContainer>
					);
				}}
			/>
		</>
	);
};
